import { labelFor, copyFor } from '@/lib/fileRequestCategories';

/**
 * The most recent CLOSED file request, as a quiet collapsed card.
 *
 * Exists because a resolved request used to vanish from every drawer surface —
 * all of them select on `status === 'open'` — so an admin arriving from the
 * "client responded" email found no evidence a request had ever existed
 * (Angelle + John, 2026-08-06). This is that evidence: the ask, how it closed,
 * and what the client said, collapsed so a settled exchange never competes with
 * live work.
 *
 * STAFF AND EDITOR ONLY. Clients are deliberately never sent closed requests
 * (the detail endpoint ships them the open one or nothing), and that stays —
 * their record of a closed ask is the closed-notice + email, not a history UI.
 *
 * One request, not a list: the drawer is not an archive, and the email that
 * brought someone here is always about the most recent close. `resolution` is
 * the single source for how it ended, same field the audit trail uses.
 */

const RESOLUTION_COPY_KEY = {
  client_response: 'shared.trace_res_client_uploaded',
  editor_resolved: 'shared.trace_res_editor_resolved',
  admin_resolve: 'shared.trace_res_admin_resolve',
  admin_cancel: 'shared.trace_res_admin_cancel',
  superseded_by_editor: 'shared.trace_res_superseded',
};

const fmtDay = (iso) => {
  if (!iso) return '';
  try {
    return new Date(iso).toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
  } catch { return ''; }
};

export function FileRequestTrace({ project, categories, copy }) {
  const closed = (project.file_requests || []).filter(r => r && r.status !== 'open');
  if (closed.length === 0) return null;
  const r = closed.reduce((a, b) => ((a.request_number || 0) >= (b.request_number || 0) ? a : b));
  const cat = r.category || '';
  const last = (r.responses || []).slice(-1)[0];
  const resKey = RESOLUTION_COPY_KEY[r.resolution];
  const when = fmtDay(r.resolved_at);

  return (
    <details className="rounded-xl bg-ug-raised border border-ug-border px-5 py-4"
             data-testid="file-request-trace">
      <summary className="tap-auto cursor-pointer list-none flex items-center justify-between gap-2">
        <span className="font-sans text-[9px] font-semibold uppercase tracking-wider text-slate-500 truncate">
          {copyFor(copy, 'shared.trace_title', cat)}
          {when ? ` · closed ${when}` : ''}
        </span>
        <span className="font-sans text-[9px] text-zinc-600 shrink-0">details</span>
      </summary>
      <div className="mt-3 space-y-3">
        <div className="rounded-md bg-ug-surface border border-ug-border px-3 py-2.5"
             data-testid="file-request-trace-ask">
          <span className="font-sans text-[9px] font-semibold uppercase tracking-wider text-zinc-500 block">
            {labelFor(categories, cat)}
          </span>
          <p className="font-sans text-[12px] text-zinc-200 mt-1.5 leading-relaxed">{r.note}</p>
        </div>
        {resKey && (
          <p className="font-sans text-[11px] text-zinc-400" data-testid="file-request-trace-resolution">
            {copyFor(copy, resKey, cat)}
          </p>
        )}
        {last?.note ? (
          <div className="rounded-md border border-ug-border px-3 py-2.5"
               data-testid="file-request-trace-reply">
            <span className="font-sans text-[9px] font-semibold uppercase tracking-wider text-zinc-500 block">
              {copyFor(copy, 'shared.client_said_label', cat)}
            </span>
            <p className="font-sans text-[12px] text-zinc-200 mt-1.5 leading-relaxed">{last.note}</p>
          </div>
        ) : !last && (
          <p className="font-sans text-[10px] text-zinc-500" data-testid="file-request-trace-no-reply">
            {copyFor(copy, 'shared.trace_no_reply', cat)}
          </p>
        )}
      </div>
    </details>
  );
}

export default FileRequestTrace;
